import { useEffect, useState, useRef } from 'react'
import { ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { getSavedSites } from '@/services/apis/api'
import { SavedSitesResponse, Site, SelectedSite } from '../Settings/savedSites'
import { useAppDispatch } from '@/hooks/storeHooks/storehooks'
import {
  setDrillDown,
  setpayloadDef,
  setSiteDetails,
} from '@/redux-store/slices/menuItemSlice/menuItemSlice'
import {
  BuildingSavedFiltersIcon,
  SavedSitesLocationPinIcon,
  SettingsGearIcon,
} from '@/assets/svgIcons'
import { SaveIcon } from '@/assets/icons/SaveIcon'
import SaveViewModal from '../Settings/saveViewModal'

export default function SavedSitesDropdown() {
  const [open, setOpen] = useState(false)
  const [savedSites, setSavedSites] = useState<Site[]>([])
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [newViewName, setNewViewName] = useState('')
  const [newViewDescription, setNewViewDescription] = useState('')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const fetchSites = async () => {
    try {
      const res = (await getSavedSites()) as SavedSitesResponse
      const sites = res?.data?.data?.sites || []
      setSavedSites(sites)
    } catch (error) {
      console.error('Failed to fetch saved sites:', error)
    }
  }

  useEffect(() => {
    fetchSites()
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  const displaySites = savedSites

  const handleDrillDown = (site: Site) => {
    navigate('/home')
    dispatch(
      setpayloadDef({
        siteId: site.selectedSites.map((s: SelectedSite) =>
          String(s.Rev_io_Account_ID__c || s.Id),
        ),
        customerId: site.type === 'parentCustomer' ? [site.value] : [],
        billingSubGroup: site.type === 'billSubgroup' ? site.label : undefined,
        customerName: site.type === 'parentCustomer' ? site.label : undefined,
      }),
    )
    dispatch(setSiteDetails(site.selectedSites))
    dispatch(setDrillDown(true))
    setOpen(false)
  }

  return (
    <div className='relative' ref={dropdownRef}>
      {/* Trigger */}
      <button
        onClick={() => setOpen(!open)}
        className='relative flex justify-center items-center gap-1.5 px-3 py-1 rounded-[0.5rem] border border-solid border-border bg-tertiary cursor-pointer text-sm text-primary-foreground'
      >
        <span>Saved Sites</span>
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Card */}
      {open && (
        <div className='absolute left-1/2 -translate-x-1/2 mt-3 w-[380px] ml-[120px] bg-primary border border-border rounded-xl shadow-xl z-[9999] overflow-hidden'>
          <div className='px-4 pt-3 pb-1 text-sm font-bold text-primary-foreground'>
            {/* {displaySites.length > 0 && ( */}
            <div className='mb-3'>
              <SaveCurrentViewButton
                onClick={() => {
                  setNewViewName('')
                  setNewViewDescription('')
                  setIsSaveModalOpen(true)
                  setOpen(false)
                }}
              />
            </div>
            {/* )} */}
            Saved Sites
          </div>

          <div className='max-h-[300px] overflow-y-auto spec-scroll divide-y divide-gray-100'>
            {displaySites.length === 0 ? (
              <div className='p-6 text-center text-primary-foreground text-xs font-normal'>
                No saved sites found.
              </div>
            ) : (
              displaySites.map((item, idx) => {
                const displayTags = item.selectedSites?.slice(0, 3) || []
                const moreCount =
                  item.selectedSites && item.selectedSites.length > 3
                    ? item.selectedSites.length - 3
                    : 0

                return (
                  <div
                    key={item.id || idx}
                    className='p-2 hover:bg-primary-hover transition-colors cursor-pointer'
                    onClick={() => handleDrillDown(item)}
                  >
                    <div className='flex gap-3 items-start'>
                      <div className='flex items-center justify-center p-1.5 rounded-lg shrink-0 mt-0.5'>
                        <BuildingSavedFiltersIcon />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='font-semibold text-primary-foreground text-sm leading-snug truncate'>
                          {item.name}
                        </p>
                        <p className='text-xs text-secondary-foreground font-normal mt-0.5 leading-snug max-w-[14rem] truncate'>
                          {item.description}
                        </p>

                        {/* Tags */}
                        <div className='flex flex-wrap gap-1 mt-1'>
                          {displayTags.map((site: SelectedSite, i: number) => (
                            <span
                              key={site.Id || i}
                              className='flex items-center gap-1 text-[10px] font-normal bg-tertiary px-2 py-0.5 rounded-full text-secondary-foreground'
                            >
                              <SavedSitesLocationPinIcon />
                              {site.Name || site.label}
                            </span>
                          ))}

                          {moreCount > 0 && (
                            <span className='flex items-center text-[10px] font-semibold bg-[#EFF6FF] px-2 py-0.5 rounded-full text-[#1D4ED8] hover:bg-[#DBEAFE] transition-colors'>
                              +{moreCount} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Footer */}
          <div className='px-4 pb-3 pt-1 flex flex-col bg-primary w-full'>
            <button
              onClick={() => {
                navigate('/settings', { state: { activeTab: 'Saved Sites' } })
                setOpen(false)
              }}
              className='flex items-center justify-center gap-2 px-3 py-1 text-sm font-semibold text-accent transition-colors shadow-sm w-full'
            >
              <SettingsGearIcon />
              Manage All
            </button>
          </div>
        </div>
      )}

      <SaveViewModal
        isOpen={isSaveModalOpen}
        viewName={newViewName}
        viewDescription={newViewDescription}
        onViewNameChange={setNewViewName}
        onViewDescriptionChange={setNewViewDescription}
        onSave={() => {}}
        onClose={() => setIsSaveModalOpen(false)}
        defaultType='site'
        onSaveSuccess={fetchSites}
      />
    </div>
  )
}

const SaveCurrentViewButton = ({ onClick }: { onClick?: () => void }) => {
  return (
    <button
      onClick={onClick}
      className='flex items-center justify-center gap-1.5 rounded-[0.5rem] border border-border bg-secondary px-3 py-1.5 text-sm font-semibold text-accent transition-colors hover:bg-[#EAF1FB] w-full'
    >
      <SaveIcon />
      Save Current View
    </button>
  )
}
